import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../utils/UserContext';
import { rolesAccess, isStaff } from '../../data/map';
import { useValidationFlag } from '../hooks/useValidationFlag';
import { useCasualAccess } from '../../utils/casualAccess';

const ACTIVE = '#38ef7d';
const strokeFor = (active) => (active ? ACTIVE : 'rgba(255,255,255,0.5)');

// ─── Icons ───────────────────────────────────────────────────
const HomeIcon = (active) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={strokeFor(active)}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" />
    <path d="M9 22V12h6v10" />
  </svg>
);

const PaymentsIcon = (active) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={strokeFor(active)}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
  </svg>
);

const LeaveIcon = (active) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={strokeFor(active)}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2"  x2="16" y2="6" />
    <line x1="8"  y1="2"  x2="8"  y2="6" />
    <line x1="3"  y1="10" x2="21" y2="10" />
  </svg>
);

const UpdateIcon = (active) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={strokeFor(active)}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 11-3-6.7L21 8" />
    <polyline points="21 3 21 8 16 8" />
  </svg>
);

// Accounts — user in a circle
const AccountIcon = (active) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={strokeFor(active)}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0116 0" />
  </svg>
);

// "New Form" / "New Ticket" — document with a plus
const NewFormIcon = (active) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={strokeFor(active)}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="12" y1="12" x2="12" y2="18" />
    <line x1="9"  y1="15" x2="15" y2="15" />
  </svg>
);

// Work Availability (casual staff) — calendar with a tick
const AvailabilityIcon = (active) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={strokeFor(active)}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2"  x2="16" y2="6" />
    <line x1="8"  y1="2"  x2="8"  y2="6" />
    <line x1="3"  y1="10" x2="21" y2="10" />
    <polyline points="8.5 15.5 11 18 15.5 13.5" />
  </svg>
);

// ─── Nav items per audience ──────────────────────────────────
const HOME = { id: 'home', label: 'Home', route: '/profile', icon: HomeIcon };

const buildNavItems = (user, isCasual) => {
  const group   = user?.group   || '';
  const userJob = user?.userJob || '';

  // IGLU South Yarra: Home + New Form (departure/upload/ticket page).
  // `target` tells the page which outer/inner tab to land on, so the button works
  // even when you're already on that route but on a different tab.
  if (group === 'IGLU South Yarra') {
    return [
      HOME,
      { id: 'newform', label: 'New Form', route: '/igluaddsm', icon: NewFormIcon,
        target: { tab: 'workflowclientim', inner: 'newformm' }, first: true },
      { id: 'update', label: 'Update', route: '/igluaddsm', icon: AccountIcon,
        target: { tab: 'accountsclientim', inner: 'ticketupdatesm' } },
    ];
  }

  // Any other client: Home + New Ticket + Accounts
  if (group === 'Client') {
    return [
      HOME,
      { id: 'newticket', label: 'New Ticket', route: '/clientaddsm', icon: NewFormIcon,
        target: { tab: 'workflowclientm', inner: 'clientsticketm' }, first: true },
      { id: 'accounts', label: 'Accounts', route: '/clientaddsm', icon: AccountIcon,
        target: { tab: 'accountsclientm', inner: 'passwordm' } },
    ];
  }

  // SNL staff — unchanged: Home, Payments, then Leave (supervisor+) or Update
  const isSupervisorOrAbove = rolesAccess.supervisor.includes(userJob);
  const third = isSupervisorOrAbove
    ? { id: 'leave',  label: 'Leave',  route: '/superclientaddsm', icon: LeaveIcon }
    : { id: 'update', label: 'Update', route: '/staffaddsm',       icon: UpdateIcon };

  const items = [
    HOME,
    { id: 'payments', label: 'Payments', route: '/personalonboardm', icon: PaymentsIcon },
    third,
  ];

  // Casual staff (email + mobile matched "Casual Work Positions TFN") get a
  // fourth button. Everyone else keeps the three above exactly as before.
  if (isCasual) {
    items.splice(2, 0, {
      id: 'availability', label: 'Availability', route: '/workavailabilitym', icon: AvailabilityIcon,
    });
  }

  return items;
};

// ─── FooterNav ───────────────────────────────────────────────
const FooterNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSet } = useValidationFlag();
  const { user, setUser } = useUser();

  // ProfileHome normally sets user.isCasual before any inner page renders; this
  // covers deep links (e.g. straight to /workavailabilitym) where it hasn't run.
  // Same cached call as ProfileHome, so no second request. Hooks stay above the
  // early return below.
  const { access } = useCasualAccess(!!user && isStaff(user) && user.isCasual === undefined);
  useEffect(() => {
    if (access && user && user.isCasual !== access.casual) {
      setUser({ ...user, isCasual: access.casual });
    }
  }, [access]);

  const isCasual = user?.isCasual ?? access?.casual ?? false;
  const navItems = buildNavItems(user, isCasual);

  const hiddenRoutes = ['/', '/index', '/validlogin', '/validationpage'];
  if (isSet() || hiddenRoutes.includes(location.pathname)) {
    return null;
  }

  // four buttons need a little less horizontal padding to sit inside 480px
  const btnPad = navItems.length > 3 ? '8px 10px' : '8px 16px';

  return (
    <>
      <style>{`
        @media (min-width: 1025px) {
          .footer-nav-mobile { display: none !important; }
        }
      `}</style>
      <nav
        className="footer-nav-mobile"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)',
          borderTop: '1px solid rgba(255, 255, 255, 0.12)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 -4px 30px rgba(0, 0, 0, 0.4)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            height: '68px',
            maxWidth: '480px',
            margin: '0 auto',
          }}
        >
          {navItems.map((item) => {
            const onRoute = location.pathname === item.route;
            const currentTab = location.state?.tab;              // set by our own navigate(); absent on a plain visit
            const isActive = !item.target
              ? onRoute
              : onRoute && (currentTab ? currentTab === item.target.tab : !!item.first);

            return (
              <button
                key={item.id}
                onClick={() =>
                  item.target
                    // fresh `reset` each tap → location changes even on the same path → page switches tab
                    ? navigate(item.route, { state: { ...item.target, reset: Date.now() } })
                    : navigate(item.route)
                }
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: btnPad,
                  borderRadius: '16px',
                  position: 'relative',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  ...(isActive && { background: 'rgba(56, 239, 125, 0.1)' }),
                }}
              >
                {isActive && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '2px',
                      width: '4px',
                      height: '4px',
                      borderRadius: '50%',
                      background: ACTIVE,
                      boxShadow: `0 0 8px ${ACTIVE}`,
                    }}
                  />
                )}

                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {item.icon(isActive)}
                </span>

                <span
                  style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontSize: '0.7rem',
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? ACTIVE : 'rgba(255, 255, 255, 0.5)',
                    letterSpacing: '0.5px',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default FooterNav;